import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_PALETTE, type Density, type PaletteKey } from "../theme/palettes";

/** Which detail slide-over is open, and for which record id. */
export type DetailKind =
  | "namespace" | "document" | "operation" | "job"
  | "repo" | "member" | "key" | "profile";

export type ModalKind =
  | "createKey" | "connectRepo" | "invite" | "newWorkspace" | "upsertDoc"
  | "renameNamespace" | "deleteNamespace" | "workspaceMenu" | "renameGeneric";

export interface Detail {
  kind: DetailKind;
  id: string;
}

export interface RenameTarget {
  kind: "repo" | "key";
  id: string;
  current: string;
}

interface UiState {
  // appearance — persisted
  palette: PaletteKey;
  density: Density;
  scanlines: boolean;
  setPalette: (p: PaletteKey) => void;
  setDensity: (d: Density) => void;
  setScanlines: (on: boolean) => void;

  // overlays — transient
  detail: Detail | null;
  modal: ModalKind | null;
  renameTarget: RenameTarget | null;
  openDetail: (kind: DetailKind, id: string) => void;
  closeDetail: () => void;
  openModal: (m: ModalKind) => void;
  closeModal: () => void;
  openRename: (t: RenameTarget) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      palette: DEFAULT_PALETTE,
      density: "comfortable",
      scanlines: true,
      setPalette: (palette) => set({ palette }),
      setDensity: (density) => set({ density }),
      setScanlines: (scanlines) => set({ scanlines }),

      detail: null,
      modal: null,
      renameTarget: null,
      openDetail: (kind, id) => set({ detail: { kind, id }, modal: null }),
      closeDetail: () => set({ detail: null }),
      openModal: (modal) => set({ modal, detail: null }),
      closeModal: () => set({ modal: null, renameTarget: null }),
      openRename: (renameTarget) =>
        set({ renameTarget, modal: "renameGeneric", detail: null }),
    }),
    {
      name: "vf-console-ui",
      // Only appearance survives a reload; overlay state must not.
      partialize: (s) => ({ palette: s.palette, density: s.density, scanlines: s.scanlines }),
    },
  ),
);

// Exposed under /dev so the UI can be driven from the browser console (and by
// automated fidelity checks) without wiring test ids into every row.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__vfUi = useUiStore;
}

/** Mirrors appearance onto <html> so CSS can key off it before React paints. */
export function applyChrome(palette: PaletteKey, density: Density): void {
  const root = document.documentElement;
  root.setAttribute("data-vf-theme", palette);
  root.setAttribute("data-vf-density", density);
}
